import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";
import {
  Broadcast, Plus, ArrowClockwise, Trash, Copy, Check, SignOut,
  PaperPlaneTilt, ListDashes, PuzzlePiece, LinkSimple, ChartBar,
} from "@phosphor-icons/react";
import NewPostModal from "@/components/NewPostModal";
import AccountRow from "@/components/AccountRow";
import AnalyticsPanel from "@/components/AnalyticsPanel";

const PlatformIcon = ({ p, size = 14 }) => {
  if (p === "facebook") return <FaFacebook size={size} color="#1877F2" />;
  if (p === "instagram") return <FaInstagram size={size} color="#E4405F" />;
  if (p === "linkedin") return <FaLinkedin size={size} color="#0A66C2" />;
  return null;
};

const OAUTH_MESSAGES = {
  fb: {
    connected: ["success", "Facebook connected"],
    error: ["error", "Facebook connect cancelled"],
    invalid_state: ["error", "Facebook OAuth state mismatch"],
    exchange_failed: ["error", "Facebook token exchange failed"],
  },
  li: {
    connected: ["success", "LinkedIn connected"],
    error: ["error", "LinkedIn connect cancelled"],
    invalid_state: ["error", "LinkedIn OAuth state mismatch"],
    exchange_failed: ["error", "LinkedIn token exchange failed"],
  },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [capabilities, setCapabilities] = useState({});
  const [stats, setStats] = useState({ total: 0, queued: 0, scheduled: 0, posted: 0, failed: 0, clicks: 0 });
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [composerOpen, setComposerOpen] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [analyticsPost, setAnalyticsPost] = useState(null);

  // Handle OAuth-return query params (?fb=connected, ?li=error, etc.)
  useEffect(() => {
    for (const [k, v] of searchParams.entries()) {
      const map = OAUTH_MESSAGES[k];
      if (map && map[v]) {
        const [kind, msg] = map[v];
        toast[kind === "success" ? "success" : "error"](msg);
      }
    }
    if (searchParams.has("fb") || searchParams.has("li")) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadAll = useCallback(async () => {
    try {
      const [p, a, s, c] = await Promise.all([
        api.get("/posts", { params: {
          status: filterStatus === "all" ? undefined : filterStatus,
          platform: filterPlatform === "all" ? undefined : filterPlatform,
        }}),
        api.get("/accounts"),
        api.get("/posts/stats"),
        api.get("/accounts/capabilities"),
      ]);
      setPosts(p.data);
      setAccounts(a.data);
      setStats(s.data);
      setCapabilities(c.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/", { replace: true });
    }
  }, [filterStatus, filterPlatform, navigate]);

  useEffect(() => {
    if (!user) return;
    loadAll();
    const t = setInterval(loadAll, 6000);
    return () => clearInterval(t);
  }, [user, loadAll]);

  const deletePost = async (id) => {
    await api.delete(`/posts/${id}`);
    await loadAll();
    toast.success("Post removed");
  };

  const retryPost = async (id) => {
    await api.post(`/posts/${id}/retry`);
    await loadAll();
    toast.success("Retrying…");
  };

  const copyToken = async () => {
    const t = localStorage.getItem("clyde_session_token");
    if (!t) { toast.error("No token — try signing out & back in"); return; }
    await navigator.clipboard.writeText(t);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success("Token copied — paste into the extension");
  };

  const token = localStorage.getItem("clyde_session_token") || "";
  if (!user) return null;

  return (
    <div data-testid={TID.dashboardRoot} className="min-h-screen bg-[#09090B] text-white flex">
      <Toaster theme="dark" position="top-right" richColors />

      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-[280px] border-r border-[#27272A] p-5 sticky top-0 h-screen overflow-auto">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#FF4500] flex items-center justify-center" style={{ borderRadius: 2 }}>
            <Broadcast size={18} weight="fill" color="#fff" />
          </div>
          <div>
            <div className="font-display text-lg leading-none">clydeflow</div>
            <div className="overline">scheduler</div>
          </div>
        </div>

        <div className="overline mb-2">navigate</div>
        <nav className="flex flex-col gap-1 mb-6">
          <div className="flex items-center gap-2 px-3 py-2 bg-[#18181B] border-l-2 border-[#FF4500] text-sm">
            <ListDashes size={16} /> Queue
          </div>
        </nav>

        <div className="overline mb-2 flex items-center justify-between">
          <span>accounts</span>
        </div>
        <div className="flex flex-col gap-2 mb-6">
          {accounts.map((a) => (
            <AccountRow
              key={a.platform}
              account={a}
              capabilities={capabilities}
              onChanged={loadAll}
            />
          ))}
        </div>

        <div className="overline mb-2">extension</div>
        <button
          data-testid={TID.extensionTokenBtn}
          onClick={() => setTokenOpen((v) => !v)}
          className="btn btn-ghost text-sm justify-start w-full"
        >
          <PuzzlePiece size={14} /> extension token
        </button>
        {tokenOpen && (
          <div className="mt-2 border border-[#27272A] p-3 bg-black/50" style={{ borderRadius: 2 }}>
            <div className="overline mb-2">paste in extension</div>
            <div data-testid={TID.extensionTokenValue} className="font-mono text-[10px] break-all text-[#D4FF00] mb-2 max-h-24 overflow-auto">
              {token || "no token available"}
            </div>
            <button data-testid={TID.copyTokenBtn} onClick={copyToken} className="btn btn-secondary text-xs w-full">
              {copied ? <><Check size={14} weight="bold" /> copied</> : <><Copy size={14} /> copy token</>}
            </button>
          </div>
        )}

        <div className="mt-auto pt-4 border-t border-[#27272A]">
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full" />
            ) : <div className="w-9 h-9 bg-[#27272A] rounded-full" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user.name || user.email}</div>
              <div className="text-xs text-[#71717A] truncate">{user.email}</div>
            </div>
          </div>
          <button data-testid={TID.logoutBtn} onClick={async () => { await logout(); navigate("/", { replace: true }); }} className="mt-3 btn btn-ghost text-xs w-full">
            <SignOut size={12} /> sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline">workspace</div>
            <div className="font-display text-2xl">Queue & schedule</div>
          </div>
          <button data-testid={TID.newPostBtn} onClick={() => setComposerOpen(true)} className="btn btn-primary">
            <Plus weight="bold" size={16} /> new post
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 border-b border-[#27272A]">
          {[
            { k: "total", label: "total", color: "#FAFAFA" },
            { k: "queued", label: "queued", color: "#D4FF00" },
            { k: "scheduled", label: "scheduled", color: "#D4FF00" },
            { k: "posted", label: "posted", color: "#86efac" },
            { k: "clicks", label: "clicks", color: "#FF4500" },
          ].map((s, i, arr) => (
            <div key={s.k} className="p-6 border-r border-[#27272A]" style={{ borderRight: i === arr.length - 1 ? "none" : undefined }}>
              <div className="overline">{s.label}</div>
              <div className="font-display text-4xl mt-1" style={{ color: s.color }}>{stats[s.k] ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 flex items-center gap-2 flex-wrap border-b border-[#27272A]">
          <span className="overline mr-2">status</span>
          {["all", "queued", "scheduled", "posted", "failed"].map((s) => (
            <button
              key={s}
              data-testid={`${TID.filterStatus}-${s}`}
              onClick={() => setFilterStatus(s)}
              className="px-3 py-1 text-xs font-mono uppercase tracking-widest transition-colors duration-150"
              style={{
                border: `1px solid ${filterStatus === s ? "#FF4500" : "#27272A"}`,
                color: filterStatus === s ? "#FF4500" : "#A1A1AA",
                borderRadius: 2,
              }}
            >{s}</button>
          ))}
          <div className="w-px h-6 bg-[#27272A] mx-3" />
          <span className="overline mr-2">platform</span>
          {["all", "facebook", "instagram", "linkedin"].map((p) => (
            <button
              key={p}
              data-testid={`${TID.filterPlatform}-${p}`}
              onClick={() => setFilterPlatform(p)}
              className="px-3 py-1 text-xs font-mono uppercase tracking-widest transition-colors duration-150"
              style={{
                border: `1px solid ${filterPlatform === p ? "#FF4500" : "#27272A"}`,
                color: filterPlatform === p ? "#FF4500" : "#A1A1AA",
                borderRadius: 2,
              }}
            >{p}</button>
          ))}
        </div>

        <div className="p-6">
          {posts.length === 0 ? (
            <div className="border border-dashed border-[#27272A] p-12 text-center" style={{ borderRadius: 2 }}>
              <PaperPlaneTilt size={40} className="mx-auto text-[#3F3F46]" weight="fill" />
              <div className="font-display text-2xl mt-4">Nothing queued yet</div>
              <div className="text-[#71717A] text-sm mt-1">Create your first post — or click the extension icon on any page.</div>
              <button onClick={() => setComposerOpen(true)} className="btn btn-primary mt-6">
                <Plus size={14} weight="bold" /> new post
              </button>
            </div>
          ) : (
            <div className="border border-[#27272A]" style={{ borderRadius: 2 }}>
              <div className="grid grid-cols-12 gap-4 px-4 py-2 overline border-b border-[#27272A]">
                <div className="col-span-5">post</div>
                <div className="col-span-2">platforms</div>
                <div className="col-span-2">when</div>
                <div className="col-span-2">status</div>
                <div className="col-span-1 text-right">act</div>
              </div>
              {posts.map((p) => (
                <div
                  key={p.id}
                  data-testid={`${TID.postRow}-${p.id}`}
                  className="grid grid-cols-12 gap-4 px-4 py-4 border-b border-[#27272A] hover:bg-[#0f0f10] transition-colors duration-150"
                >
                  <div className="col-span-5 min-w-0 flex items-start gap-3">
                    {p.preview?.image ? (
                      <img
                        src={p.preview.image}
                        alt=""
                        className="w-12 h-12 object-cover flex-shrink-0"
                        style={{ borderRadius: 2 }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-[#18181B] flex items-center justify-center flex-shrink-0" style={{ borderRadius: 2 }}>
                        <LinkSimple size={16} className="text-[#3F3F46]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {p.preview?.title && (
                        <div className="text-sm font-medium text-[#FAFAFA] truncate">{p.preview.title}</div>
                      )}
                      <div className="flex items-center gap-1 text-[10px] text-[#71717A] truncate font-mono">
                        <LinkSimple size={10} />
                        <span className="truncate">{p.preview?.domain || p.url}</span>
                      </div>
                      <div className={`text-xs mt-1 truncate ${p.preview?.title ? "text-[#A1A1AA]" : "text-[#FAFAFA]"}`}>
                        {p.caption || <span className="text-[#71717A] italic">no caption</span>}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    {p.platforms.map((pl) => <PlatformIcon key={pl} p={pl} size={16} />)}
                  </div>
                  <div className="col-span-2 font-mono text-xs text-[#A1A1AA]">
                    {p.scheduled_at
                      ? format(new Date(p.scheduled_at), "MMM d · HH:mm")
                      : p.posted_at
                        ? `${formatDistanceToNow(new Date(p.posted_at))} ago`
                        : "asap"}
                  </div>
                  <div className="col-span-2">
                    <span className={`status-pill status-${p.status}`}>{p.status}</span>
                    {p.error && <div className="text-[10px] text-[#fca5a5] mt-1 truncate">{p.error}</div>}
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    {p.status === "posted" && (
                      <button
                        data-testid={`${TID.postAnalytics}-${p.id}`}
                        onClick={() => setAnalyticsPost(p)}
                        className="p-1.5 text-[#D4FF00] hover:bg-[#18181B] transition-colors duration-150"
                        title="View analytics"
                      >
                        <ChartBar size={14} weight="bold" />
                      </button>
                    )}
                    {p.status === "failed" && (
                      <button data-testid={`${TID.postRetry}-${p.id}`} onClick={() => retryPost(p.id)} className="p-1.5 text-[#D4FF00] hover:bg-[#18181B] transition-colors duration-150" title="Retry">
                        <ArrowClockwise size={14} weight="bold" />
                      </button>
                    )}
                    <button data-testid={`${TID.postDelete}-${p.id}`} onClick={() => deletePost(p.id)} className="p-1.5 text-[#71717A] hover:text-[#FF4500] hover:bg-[#18181B] transition-colors duration-150" title="Delete">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <NewPostModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={loadAll}
        accounts={accounts}
      />
      {analyticsPost && (
        <AnalyticsPanel post={analyticsPost} onClose={() => setAnalyticsPost(null)} />
      )}
    </div>
  );
}
